import type { ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * The visual vocabulary shared by the project board and the all-clients rollup.
 *
 * Both screens carry the same toolbar — an underline tab bar, a row of filter
 * pills, a committed meter — and they drifted apart the last time each was
 * edited in isolation. One module, both callers.
 */

/** The view switcher. A local restyle of the shared Radix `Tabs`, NOT a change
 * to `components/ui/tabs.tsx`: the segmented pill there is right for the rest of
 * the app, and every other page would inherit an underline it didn't ask for. */
export const TAB_LIST_CLASS =
  "h-auto items-end gap-[22px] rounded-none bg-transparent p-0 text-muted-foreground";

export const TAB_TRIGGER_CLASS =
  "rounded-none bg-transparent px-px pb-2.5 pt-0 text-[13.5px] font-semibold tracking-[-0.01em] text-muted-foreground shadow-none transition-colors hover:text-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-[inset_0_-2px_0_hsl(var(--primary))]";

/** The rule the tabs sit on, run across the whole content width. */
export const TAB_BAR_ROW_CLASS =
  "flex flex-wrap items-end gap-x-4 gap-y-1 border-b border-border";

/** Filter chrome: borderless at rest, tinted when it's actually filtering
 * something. An idle filter that looks like a button implies there is something
 * to do; an active one has to be obvious, because a forgotten filter is how a
 * PA concludes their tasks have vanished. */
export function filterPillClass(active: boolean): string {
  return cn(
    "inline-flex h-[26px] items-center gap-1.5 rounded-full px-2.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    active
      ? "bg-blue-50 font-semibold text-blue-700 dark:bg-blue-500/15 dark:text-blue-300"
      : "font-medium text-muted-foreground hover:bg-muted/70 hover:text-foreground",
  );
}

/**
 * A filter pill with a one-click clear.
 *
 * The `×` is a sibling button rather than a child of the trigger: nesting it
 * would be invalid markup, and clicking it would open the popover it is meant to
 * make unnecessary.
 */
export function FilterPillShell({
  active,
  onClear,
  clearLabel,
  children,
  className,
}: {
  active: boolean;
  /** Omit to render a pill with no clear affordance. */
  onClear?: () => void;
  clearLabel?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span className={cn(filterPillClass(active), active && onClear && "pr-1.5", className)}>
      {children}
      {active && onClear && (
        <button
          type="button"
          aria-label={clearLabel ?? "Clear filter"}
          onClick={onClear}
          className="-mr-0.5 rounded-full p-0.5 opacity-55 transition-opacity hover:opacity-100"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}

/** A board's identity mark: its initials over its assigned series colour. The
 * same slot the time report uses, so a board is the same colour wherever a PA
 * meets it. */
export function ProjectAvatar({
  name,
  color,
  className,
}: {
  name: string;
  color: string | undefined;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-lg text-[10px] font-bold text-white",
        className,
      )}
      style={{ background: color ?? "hsl(var(--muted-foreground))" }}
    >
      {initialsOf(name)}
    </span>
  );
}
